/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { math } from 'polished';
import { btnSizes } from './_buttons.css';
import { Hover } from './mixins/_hover.mixin';
import { BorderBottomRadius, BorderLeftRadius, BorderRightRadius, BorderTopRadius } from './mixins/_border-radius.mixin';
import { BoxShadow } from './mixins/_box-shadow.mixin';

export default $ => {
  const rules = [];

  // stylelint-disable selector-no-qualifying-type
  // Make the div behave like a button
  rules.push(css`
    .btn-group,
    .btn-group-vertical {
      position: relative;
      display: inline-flex;
      vertical-align: middle; // match .btn alignment given font-size hack above

      > .btn {
        position: relative;
        flex: 1 1 auto;

        // Bring the hover, focused, and "active" buttons to the front to overlay
        // the borders properly
        ${new Hover().include(
          $,
          css`
            z-index: 1;
          `
        )}
        &:focus,
        &:active,
        &.active {
          z-index: 1;
        }
      }
    }
  `);

  // Optional: Group multiple button groups together for a toolbar
  rules.push(css`
    .btn-toolbar {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;

      .input-group {
        width: auto;
      }
    }
  `);

  rules.push(css`
    .btn-group {
      // Prevent double borders when buttons are next to each other
      > .btn:not(:first-of-type),
      > .btn-group:not(:first-of-type) {
        margin-left: ${math(`-${$.btnBorderWidth}`)};
      }

      // Reset rounded corners
      > .btn:not(:last-child):not(.dropdown-toggle),
      > .btn-group:not(:last-child) > .btn {
        ${new BorderRightRadius(0).include($)}
      }

      > .btn:not(:first-of-type),
      > .btn-group:not(:first-of-type) > .btn {
        ${new BorderLeftRadius(0).include($)}
      }
    }
  `);

  // Sizing
  //
  // Remix the default button sizing classes into new ones for easier manipulation.
  rules.push(css`
    .btn-group-sm > .btn {
      ${btnSizes.btnSm($)}
    }
    .btn-group-lg > .btn {
      ${btnSizes.btnLg($)}
    }
  `);

  //
  // Split button dropdowns
  //
  rules.push(css`
    .dropdown-toggle-split {
      padding-right: ${math(`${$.btnPaddingX} * 0.75`)};
      padding-left: ${math(`${$.btnPaddingX} * 0.75`)};

      &::after,
      .dropup &::after,
      .dropright &::after {
        margin-left: 0;
      }

      .dropleft &::before {
        margin-right: 0;
      }
    }
  `);

  rules.push(css`
    .btn-sm + .dropdown-toggle-split {
      padding-right: ${math(`${$.btnPaddingXSm} * 0.75`)};
      padding-left: ${math(`${$.btnPaddingXSm} * 0.75`)};
    }

    .btn-lg + .dropdown-toggle-split {
      padding-right: ${math(`${$.btnPaddingXLg} * 0.75`)};
      padding-left: ${math(`${$.btnPaddingXLg} * 0.75`)};
    }
  `);

  // The clickable button for toggling the menu
  // Set the same inset shadow as the :active state
  rules.push(css`
    .btn-group.show .dropdown-toggle {
      // Show no shadow for \`.btn-link\` since it has no other button styles.
      &.btn-link {
        ${new BoxShadow('none').include($)};
      }
    }
  `);

  //
  // Vertical button groups
  //
  rules.push(css`
    .btn-group-vertical {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      > .btn,
      > .btn-group {
        width: 100%;
      }

      > .btn:not(:first-of-type),
      > .btn-group:not(:first-of-type) {
        margin-top: ${math(`-${$.btnBorderWidth}`)};
      }

      // Reset rounded corners
      > .btn:not(:last-child):not(.dropdown-toggle),
      > .btn-group:not(:last-child) > .btn {
        ${new BorderBottomRadius(0).include($)};
      }

      > .btn:not(:first-of-type),
      > .btn-group:not(:first-of-type) > .btn {
        ${new BorderTopRadius(0).include($)};
      }
    }
  `);

  // Checkbox and radio options
  //
  // In order to support the browser's form validation feedback, powered by the
  // `required` attribute, we have to "hide" the inputs via `clip`. We cannot use
  // `display: none;` or `visibility: hidden;` as that also hides the popover.
  // Simply visually hiding the inputs via `opacity` would leave them clickable in
  // certain cases which is prevented by using `clip` and `pointer-events`.
  // This way, we ensure a DOM element is visible to position the popover from.
  //
  // See https://github.com/twbs/bootstrap/pull/12794 and
  // https://github.com/twbs/bootstrap/pull/14559 for more information.
  rules.push(css`
    .btn-group-toggle {
      > .btn,
      > .btn-group > .btn {
        margin-bottom: 0; // Override default \`<label>\` value

        input[type='radio'],
        input[type='checkbox'] {
          position: absolute;
          clip: rect(0, 0, 0, 0);
          pointer-events: none;
        }
      }
    }
  `);

  return css(rules);
};
